import React, { useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Upload, FileText, CheckCircle, X, Send, Users, Building, FileSpreadsheet, MapPin, Search, UserCheck, Shield, Award, Info, DollarSign, Briefcase, Settings2, Paperclip, Contact, Percent, ArrowRight, ArrowLeft, Star, Target, Zap, TrendingUp, HelpCircle, HardDrive, Edit, Check, ExternalLink
} from 'lucide-react';

// --- Reusable UI Components ---

const FormSection = ({ title, subtitle, children }) => (
    <div className="mb-12">
        <h3 className="text-2xl font-bold text-slate-800">{title}</h3>
        {subtitle && <p className="mt-1 text-slate-500">{subtitle}</p>}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">{children}</div>
    </div>
);

const InputField = ({ label, name, value, onChange, placeholder, icon, required = false, type = "text", step, iconPosition = 'default' }) => (
    <div>
        {label && (
            <label htmlFor={name} className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                {icon && iconPosition === 'default' && React.cloneElement(icon, { size: 16, className: "text-indigo-500"})}
                {label} {required && <span className="text-red-500">*</span>}
            </label>
        )}
        <div className="relative">
            {icon && iconPosition === 'leading' && (
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    {React.cloneElement(icon, { size: 20, className: "text-slate-400"})}
                </div>
            )}
            <input
                id={name}
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                required={required}
                step={step}
                className={`w-full py-3 bg-white border border-slate-300 rounded-lg focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-300 text-slate-700 placeholder:text-slate-400 ${iconPosition === 'leading' ? 'pl-12 pr-4' : 'px-4'}`}
                placeholder={placeholder}
            />
        </div>
    </div>
);

const RadioCard = ({ name, value, onChange, options }) => (
    <div>
        <div className="grid grid-cols-2 gap-3">
            {options.map(option => (
                <label key={option.value} className={`relative flex flex-col items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${value === option.value ? 'border-indigo-500 bg-indigo-50 shadow-sm' : 'border-slate-300 bg-white hover:border-slate-400'}`}>
                    <input
                        type="radio"
                        name={name}
                        value={option.value}
                        checked={value === option.value}
                        onChange={onChange}
                        className="absolute opacity-0"
                    />
                    <div className="flex items-center gap-3">
                        {React.cloneElement(option.icon, { size: 20, className: `mb-1 ${value === option.value ? 'text-indigo-600' : 'text-slate-500'}` })}
                        <span className="font-semibold text-slate-800">{option.label}</span>
                    </div>
                </label>
            ))}
        </div>
    </div>
);

const StepIndicator = ({ currentStep, steps, visitedSteps, goToStep }) => (
    <div className="mb-12">
        <ol className="flex items-center justify-center w-full text-sm font-medium text-center">
            {steps.map((step, index) => {
                const stepIndex = index + 1;
                const isCompleted = currentStep > stepIndex;
                const isCurrent = currentStep === stepIndex;
                const isVisited = visitedSteps.has(stepIndex);
                const isClickable = isVisited && !isCurrent;

                return (
                    <React.Fragment key={step.id}>
                        <li className="flex items-center">
                            <div 
                                className={`flex flex-col items-center justify-center transition-all duration-300 ${
                                    isCurrent ? 'text-indigo-600' : isCompleted ? 'text-green-600' : 'text-slate-500'
                                } ${isClickable ? 'cursor-pointer hover:scale-105' : ''}`}
                                onClick={() => isClickable && goToStep(stepIndex)}
                            >
                                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-300 ${
                                    isCurrent ? 'border-indigo-500 bg-indigo-50' : 
                                    isCompleted ? 'border-green-500 bg-green-50' : 
                                    isVisited ? 'border-slate-400 bg-slate-50 hover:border-indigo-400 hover:bg-indigo-50' :
                                    'border-slate-300'
                                }`}>
                                    {isCompleted ? <CheckCircle size={20} /> : React.cloneElement(step.icon, { size: 20 })}
                                </div>
                                <h4 className={`mt-2 font-semibold ${isClickable ? 'hover:text-indigo-600' : ''}`}>
                                    {step.name}
                                </h4>
                            </div>
                        </li>
                        {stepIndex < steps.length && (
                            <li className="flex-auto">
                                <div className={`h-1 w-full transition-all duration-500 ease-in-out ${isCompleted ? 'bg-green-500' : 'bg-slate-200'}`} />
                            </li>
                        )}
                    </React.Fragment>
                );
            })}
        </ol>
    </div>
);


const ProposalAutomation = ({ prefilledData = null, onSubmit }) => {
    // Allow pre-filled data to be delivered via React-Router navigation state
    const location = useLocation();
    const routePrefilledData = location.state?.prefilledData || null;
    const mergedPrefilledData = prefilledData || routePrefilledData;

    const [currentStep, setCurrentStep] = useState(1);
    const [visitedSteps, setVisitedSteps] = useState(new Set([1])); // Track which steps have been visited
    
    // Initialize form data with prefilled data if provided
    const getInitialFormData = () => {
        const defaultData = {
            // Step 1: Documents
            form5500: null,
            censusFile: null,
            currentProposal: null,
            adoptionAgreement: null,
            
            // Step 2: Plan Basics
            planName: '',
            sponsorName: '',
            sponsorAddress: '',
            sponsorIndustry: '',
            planType: '401(k)',
            bundleType: 'bundled',
            reasonForReview: '',
            decisionTimeline: '',

            // Step 3: Demographics & Assets
            totalParticipants: '',
            activeParticipants: '',
            hceCount: '',
            totalAssets: '',
            pastYearContributions: '',
            currentRK: '',
            currentTPA: '',
            currentAdvisor: '',

            // Step 4: Plan Design
            isSafeHarbor: 'no',
            matchFormula: '',
            profitSharingFormula: '',
            eligibility: '',
            vestingSchedule: '',
            hasAutoEnrollment: 'no',
            autoEnrollmentDetails: '',
            allowsLoans: 'no',
            allowsHardships: 'no',

            // Step 5: Investments
            advisorFiduciaryRole: '3(21)',
            desiredArchitecture: 'open',
            currentQDIA: '',
            needsSDBA: 'no',
            advisorFeeStructure: 'asset_based',
            advisorFeeAmount: '',

            // Step 6: Recipients
            selectedRecipients: [],
            personalNote: '',
        };
        
        // If merged prefilled data is provided, merge it with defaults
        if (mergedPrefilledData) {
            return { ...defaultData, ...mergedPrefilledData };
        }
        
        return defaultData;
    };

    const [formData, setFormData] = useState(getInitialFormData());

    const steps = [
        { id: 'step1', name: 'Documents', icon: <Paperclip /> },
        { id: 'step2', name: 'Plan Basics', icon: <Info /> },
        { id: 'step3', name: 'Demographics', icon: <Users /> },
        { id: 'step4', name: 'Plan Design', icon: <Edit /> },
        { id: 'step5', name: 'Investments', icon: <TrendingUp /> },
        { id: 'step6', name: 'Recipients', icon: <Contact /> },
        { id: 'step7', name: 'Submit', icon: <Send /> },
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e, fieldName) => {
        const file = e.target.files[0];
        setFormData(prev => ({ ...prev, [fieldName]: file }));
    };

    const nextStep = () => {
        const newStep = Math.min(currentStep + 1, steps.length);
        setCurrentStep(newStep);
        setVisitedSteps(prev => new Set([...prev, newStep]));
    };
    
    const prevStep = () => {
        const newStep = Math.max(currentStep - 1, 1);
        setCurrentStep(newStep);
    };
    
    const goToStep = (step) => {
        if (visitedSteps.has(step)) {
            setCurrentStep(step);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        console.log("Final Form Data:", formData);
        
        if (onSubmit) {
            onSubmit(formData);
        } else {
            alert("Proposal request submitted successfully! Check the console for the form data.");
        }
    };

    const renderStepContent = () => {
        const commonProps = { formData, handleInputChange, handleFileChange, setFormData };
        switch (currentStep) {
            case 1: return <Step1_Documents {...commonProps} />;
            case 2: return <Step2_PlanBasics {...commonProps} />;
            case 3: return <Step3_Demographics {...commonProps} />;
            case 4: return <Step4_PlanDesign {...commonProps} />;
            case 5: return <Step5_Investments {...commonProps} />;
            case 6: return <Step6_Recipients {...commonProps} />;
            case 7: return <Step7_Submit handleSubmit={handleSubmit} />;
            default: return <Step1_Documents {...commonProps} />;
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 p-4 sm:p-8">
            <div className="max-w-8xl mx-auto">
                <div className="bg-white rounded-2xl shadow-xl border border-slate-200/80">
                    <div className="p-8 sm:p-12 border-b border-slate-200">
                        {mergedPrefilledData && (
                            <div className="mb-8 p-4 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl">
                                <div className="flex items-center gap-3">
                                    <div className="flex-shrink-0">
                                        <Zap className="w-6 h-6 text-emerald-600" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-emerald-900">AI Auto-Fill Complete</h4>
                                        <p className="text-emerald-700 text-sm mt-1">
                                            Form 5500 data from <strong>{mergedPrefilledData.planName || 'Unknown Plan'}</strong> has been automatically populated{mergedPrefilledData.form5500 && mergedPrefilledData.form5500.isUrlFile ? ' and the Form 5500 filing has been attached' : ''}. 
                                            Please review and update any sections as needed.
                                        </p>
                                        {mergedPrefilledData._sourceData && (
                                            <div className="mt-2 text-xs text-emerald-600 bg-emerald-100 inline-block px-3 py-1 rounded-full">
                                                EIN: {mergedPrefilledData._sourceData.ein} | Filing Year: {mergedPrefilledData._sourceData.mostRecentFilingYear}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                        <StepIndicator currentStep={currentStep} steps={steps} visitedSteps={visitedSteps} goToStep={goToStep} />
                    </div>
                    <div className="p-8 sm:p-12">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={currentStep}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.3 }}
                            >
                                {renderStepContent()}
                            </motion.div>
                        </AnimatePresence>

                        {/* Navigation */}
                        <div className="mt-16 pt-8 border-t border-slate-200 flex justify-between items-center">
                            <div>
                                {currentStep > 1 && (
                                    <button type="button" onClick={prevStep} className="flex items-center gap-2 px-6 py-3 bg-slate-200 text-slate-700 font-semibold rounded-lg hover:bg-slate-300 transition-all">
                                        <ArrowLeft size={18} />
                                        Back
                                    </button>
                                )}
                            </div>
                            <div>
                                {currentStep < steps.length && (
                                    <button type="button" onClick={nextStep} className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-all shadow-sm">
                                        Next
                                        <ArrowRight size={18} />
                                    </button>
                                )}
                                {currentStep === steps.length && (
                                     <button type="button" onClick={handleSubmit} className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-all shadow-sm">
                                        Finalize & Submit
                                        <Send size={18} />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="text-center mt-4 text-sm text-slate-400">
                    <p>Proposal Automation Engine &copy; {new Date().getFullYear()}</p>
                </div>
            </div>
        </div>
    );
};


// --- Step Components ---

const Step1_Documents = ({ formData, handleFileChange }) => (
    <>
        <FormSection title="Upload Documents First" subtitle="Start by uploading your plan documents. Our AI will analyze them to automatically populate the following sections, saving you time and ensuring accuracy.">
            <FileUploadZone label="Upload Form 5500" icon={<FileText/>} fieldName="form5500" file={formData.form5500} onChange={handleFileChange} />
            <FileUploadZone label="Upload Census File" icon={<FileSpreadsheet/>} fieldName="censusFile" file={formData.censusFile} onChange={handleFileChange} />
            <FileUploadZone label="Upload Current Proposal" icon={<FileText/>} fieldName="currentProposal" file={formData.currentProposal} onChange={handleFileChange} />
            <FileUploadZone label="Upload Adoption Agreement" icon={<Award/>} fieldName="adoptionAgreement" file={formData.adoptionAgreement} onChange={handleFileChange} />
        </FormSection>
        <div className="mt-8 p-6 bg-indigo-50 border border-indigo-200 rounded-xl">
            <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                    <Zap className="w-6 h-6 text-indigo-600 mt-1" />
                </div>
                <div>
                    <h4 className="font-semibold text-indigo-900 mb-2">AI-Powered Auto-Fill</h4>
                    <p className="text-indigo-700 text-sm leading-relaxed">
                        Once you upload your documents, click "Next" and our AI will automatically extract and populate plan details, demographics, design features, and investment information from your files. You can always review and edit the extracted data in the following sections.
                    </p>
                </div>
            </div>
        </div>
    </>
);

const Step2_PlanBasics = ({ formData, handleInputChange }) => (
    <>
        <FormSection title="Plan & Sponsor Basics" subtitle="Start with high-level details about the plan and the sponsoring company.">
            <InputField label="Plan Name" name="planName" value={formData.planName} onChange={handleInputChange} placeholder="e.g., ABC Company 401(k) Plan" icon={<Edit/>} required />
            <InputField label="Sponsor Company Name" name="sponsorName" value={formData.sponsorName} onChange={handleInputChange} placeholder="e.g., ABC Company, Inc." icon={<Building/>} required />
            <InputField label="Sponsor Address" name="sponsorAddress" value={formData.sponsorAddress} onChange={handleInputChange} placeholder="123 Main St, Anytown, USA" icon={<MapPin/>} required />
            <InputField label="Sponsor Industry" name="sponsorIndustry" value={formData.sponsorIndustry} onChange={handleInputChange} placeholder="e.g., Technology, Manufacturing" icon={<Briefcase/>} />
        </FormSection>
        <FormSection title="Proposal Context" subtitle="Help wholesalers understand the 'why' behind this request.">
            <div className="md:col-span-2">
              <InputField label="Primary Reason for Review" name="reasonForReview" value={formData.reasonForReview} onChange={handleInputChange} placeholder="e.g., High fees, poor service, fiduciary concerns, routine benchmark..." icon={<Target/>} required />
            </div>
            <InputField label="Decision Timeline" name="decisionTimeline" value={formData.decisionTimeline} onChange={handleInputChange} placeholder="e.g., By end of Q4 2025" icon={<Zap/>} />
        </FormSection>
    </>
);

const Step3_Demographics = ({ formData, handleInputChange }) => (
    <>
        <FormSection title="Participant & Asset Details" subtitle="Provide a snapshot of the plan's size and scope.">
            <InputField label="Total Participants (incl. termed)" name="totalParticipants" type="number" value={formData.totalParticipants} onChange={handleInputChange} placeholder="e.g., 75" icon={<Users/>} required />
            <InputField label="Active Participants" name="activeParticipants" type="number" value={formData.activeParticipants} onChange={handleInputChange} placeholder="e.g., 60" icon={<UserCheck/>} required />
            <InputField label="Number of HCEs" name="hceCount" type="number" value={formData.hceCount} onChange={handleInputChange} placeholder="Highly Compensated Employees" icon={<Star/>} />
            <InputField label="Total Plan Assets ($)" name="totalAssets" type="number" value={formData.totalAssets} onChange={handleInputChange} placeholder="e.g., 5000000" icon={<DollarSign/>} required />
            <InputField label="Last Year's Total Contributions ($)" name="pastYearContributions" type="number" value={formData.pastYearContributions} onChange={handleInputChange} placeholder="e.g., 350000" icon={<TrendingUp/>} required />
        </FormSection>
        <FormSection title="Current Providers" subtitle="Who are the incumbent service providers for the plan?">
             <InputField label="Current Recordkeeper" name="currentRK" value={formData.currentRK} onChange={handleInputChange} placeholder="e.g., Fidelity, Empower" icon={<HardDrive/>} />
             <InputField label="Current TPA (if unbundled)" name="currentTPA" value={formData.currentTPA} onChange={handleInputChange} placeholder="e.g., Pinnacle Plan Services" icon={<Building/>} />
             <InputField label="Current Financial Advisor" name="currentAdvisor" value={formData.currentAdvisor} onChange={handleInputChange} placeholder="e.g., John Smith Financial" icon={<Contact/>} />
        </FormSection>
    </>
);

const Step4_PlanDesign = ({ formData, handleInputChange }) => (
    <>
      <FormSection title="Core Plan Provisions" subtitle="These rules are critical for accurate administration and pricing.">
          <InputField label="Eligibility Requirements" name="eligibility" value={formData.eligibility} onChange={handleInputChange} placeholder="e.g., Age 21, 1000 hours, 3 months service" icon={<Edit/>} required />
          <InputField label="Vesting Schedule" name="vestingSchedule" value={formData.vestingSchedule} onChange={handleInputChange} placeholder="e.g., 6-year graded, 2-year cliff" icon={<Percent/>} required />
          <div className="md:col-span-2">
            <InputField label="Employer Match Formula" name="matchFormula" value={formData.matchFormula} onChange={handleInputChange} placeholder="e.g., 100% of first 3%, 50% of next 2%" icon={<DollarSign/>} />
          </div>
          <div className="md:col-span-2">
            <InputField label="Profit Sharing Formula" name="profitSharingFormula" value={formData.profitSharingFormula} onChange={handleInputChange} placeholder="e.g., Discretionary, Pro-rata, New Comparability" icon={<Briefcase/>} />
          </div>
      </FormSection>
      <FormSection title="Plan Features" subtitle="Select the features currently enabled in the plan.">
          <RadioCard name="isSafeHarbor" value={formData.isSafeHarbor} onChange={handleInputChange} options={[{value: 'yes', label: 'Safe Harbor', icon: <Shield/>}, {value: 'no', label: 'Not Safe Harbor', icon: <X/>}]} />
          <RadioCard name="hasAutoEnrollment" value={formData.hasAutoEnrollment} onChange={handleInputChange} options={[{value: 'yes', label: 'Auto-Enroll', icon: <TrendingUp/>}, {value: 'no', label: 'No Auto-Enroll', icon: <X/>}]} />
          {formData.hasAutoEnrollment === 'yes' && (
              <div className="md:col-span-2">
                  <InputField label="Auto-Enrollment Details" name="autoEnrollmentDetails" value={formData.autoEnrollmentDetails} onChange={handleInputChange} placeholder="e.g., QACA at 3%, 1% annual increase to 10%" icon={<Info/>} />
              </div>
          )}
          <RadioCard name="allowsLoans" value={formData.allowsLoans} onChange={handleInputChange} options={[{value: 'yes', label: 'Allows Loans', icon: <CheckCircle/>}, {value: 'no', label: 'No Loans', icon: <X/>}]} />
          <RadioCard name="allowsHardships" value={formData.allowsHardships} onChange={handleInputChange} options={[{value: 'yes', label: 'Allows Hardships', icon: <CheckCircle/>}, {value: 'no', label: 'No Hardships', icon: <X/>}]} />
      </FormSection>
    </>
);

const Step5_Investments = ({ formData, handleInputChange }) => (
    <>
      <FormSection title="Advisor & Fiduciary Role" subtitle="Define your role and fee structure for the new proposal.">
        <RadioCard name="advisorFiduciaryRole" value={formData.advisorFiduciaryRole} onChange={handleInputChange} options={[{value: '3(21)', label: '3(21) Advisor', icon: <UserCheck/>}, {value: '3(38)', label: '3(38) Manager', icon: <Shield/>}]} />
        <InputField label="Qualified Default Investment (QDIA)" name="currentQDIA" value={formData.currentQDIA} onChange={handleInputChange} placeholder="e.g., Vanguard Target Date 20XX Series" icon={<Target/>} />
        <RadioCard name="advisorFeeStructure" value={formData.advisorFeeStructure} onChange={handleInputChange} options={[{value: 'asset_based', label: 'Asset-Based', icon: <Percent/>}, {value: 'flat_fee', label: 'Flat Fee', icon: <DollarSign/>}]} />
        <InputField label="Advisor Fee Amount" name="advisorFeeAmount" value={formData.advisorFeeAmount} onChange={handleInputChange} placeholder="e.g., 0.50 or 5000" icon={<DollarSign/>} required />
      </FormSection>
      <FormSection title="Platform Requirements" subtitle="Specify the investment platform capabilities you need.">
        <RadioCard name="desiredArchitecture" value={formData.desiredArchitecture} onChange={handleInputChange} options={[{value: 'open', label: 'Open Architecture', icon: <Zap/>}, {value: 'flexible', label: 'Flexible / Preferred', icon: <Settings2/>}]} />
        <RadioCard name="needsSDBA" value={formData.needsSDBA} onChange={handleInputChange} options={[{value: 'yes', label: 'Needs SDBA', icon: <CheckCircle/>}, {value: 'no', label: 'No SDBA', icon: <X/>}]} />
      </FormSection>
    </>
);

const FileUploadZone = ({ label, icon, fieldName, file, onChange }) => (
    <div className="text-center p-6 border-2 border-dashed border-slate-300 rounded-lg bg-slate-50 hover:bg-slate-100 hover:border-indigo-400 transition-all">
        {React.cloneElement(icon, {size: 32, className: "mx-auto text-slate-400 mb-2"})}
        <label htmlFor={fieldName} className="font-semibold text-indigo-600 cursor-pointer hover:text-indigo-800">
            {label}
        </label>
        <p className="text-xs text-slate-500 mt-1">PDF, DOCX, XLSX, or CSV</p>
        <input type="file" id={fieldName} className="hidden" onChange={(e) => onChange(e, fieldName)} />
        {file && (
            <div className="mt-3">
                <div className="flex items-center justify-center gap-2 text-sm font-medium text-green-700 mb-2">
                    <CheckCircle size={16} />
                    <p className="truncate max-w-xs">{file.name}</p>
                </div>
                {file.isUrlFile && (
                    <div className="space-y-2">
                        <div className="flex items-center justify-center gap-2 text-xs text-blue-600">
                            <span className="bg-blue-100 px-2 py-1 rounded-full font-medium">
                                Auto-attached from Form 5500 ({file.filingYear})
                            </span>
                        </div>
                        <div className="flex items-center justify-center gap-3">
                            <button
                                type="button"
                                onClick={() => window.open(file.url, '_blank')}
                                className="text-xs bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1"
                            >
                                <ExternalLink size={12} />
                                View PDF
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    onChange({ target: { files: [null] } }, fieldName);
                                }}
                                className="text-xs bg-red-100 text-red-600 px-3 py-1 rounded-lg hover:bg-red-200 transition-colors flex items-center gap-1"
                            >
                                <X size={12} />
                                Remove
                            </button>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                            Filed: {file.dateReceived ? new Date(file.dateReceived).toLocaleDateString() : 'Unknown'}
                        </p>
                    </div>
                )}
            </div>
        )}
    </div>
);


// --- START: Revamped Step 6 ---

const getInitials = (name) => {
    if (!name) return '??';
    // Strip content in parentheses (like affiliations) before getting initials
    const cleanedName = name.replace(/\s*\(.*\)\s*/, '').trim();
    const words = cleanedName.split(' ');
    // For "John Smith" -> "JS"
    if (words.length > 1) {
        return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    }
    // For "Fidelity" -> "FI"
    return cleanedName.substring(0, 2).toUpperCase();
};

const RecipientCard = ({ recipient, isSelected, onToggle }) => (
    <div
        onClick={() => onToggle(recipient.id)}
        className={`relative p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 flex items-center gap-4 ${
            isSelected
                ? 'bg-indigo-50 border-indigo-500 shadow-md'
                : 'bg-white border-slate-300 hover:border-slate-400 hover:shadow-sm'
        }`}
    >
        {isSelected && (
            <div className="absolute top-3 right-3 text-white bg-indigo-500 rounded-full flex items-center justify-center w-5 h-5">
                <Check size={12} strokeWidth={3} />
            </div>
        )}
        <div className={`w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center font-bold text-lg ${isSelected ? 'bg-indigo-500 text-white' : 'bg-slate-200 text-slate-600'}`}>
            {getInitials(recipient.name)}
        </div>
        <div className="overflow-hidden">
            <p className="font-semibold text-slate-800 truncate">{recipient.name}</p>
            <p className="text-sm text-slate-500 truncate">{recipient.email}</p>
        </div>
    </div>
);

const RecipientGroup = ({ title, subtitle, list, selectedRecipients, onToggle, onSelectAll }) => {
    const isAllSelected = list.length > 0 && list.every(r => selectedRecipients.includes(r.id));
    
    return (
        <div className="mb-12">
            <div className="flex flex-wrap justify-between items-center gap-4 pb-4 border-b border-slate-200 mb-6">
                <div>
                    <h3 className="text-xl font-bold text-slate-800">{title}</h3>
                    <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold text-slate-600 hover:text-indigo-600 transition-colors">
                    <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={(e) => onSelectAll(list, e.target.checked)}
                        className="w-5 h-5 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                    />
                    Select All in this List
                </label>
            </div>

            {list.length > 0 ? (
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {list.map(recipient => (
                        <RecipientCard 
                            key={recipient.id}
                            recipient={recipient}
                            isSelected={selectedRecipients.includes(recipient.id)}
                            onToggle={onToggle}
                        />
                    ))}
                </div>
            ) : (
                <div className="text-center py-8 px-4 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                    <p className="text-slate-500 font-medium">No recipients match your search.</p>
                </div>
            )}
        </div>
    );
};

const Step6_Recipients = ({ formData, setFormData, handleInputChange }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const selectedRecipients = formData.selectedRecipients || [];

    const filterRecipients = (list) => {
        if (!searchQuery) return list;
        return list.filter(r => 
            r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            r.email.toLowerCase().includes(searchQuery.toLowerCase())
        );
    };

    const filteredWholesalers = useMemo(() => filterRecipients(TRUSTED_WHOLESALERS), [searchQuery]);
    const filteredPartners = useMemo(() => filterRecipients(SPECIAL_PARTNERS), [searchQuery]);
    const filteredContacts = useMemo(() => filterRecipients(PERSONAL_CONTACTS), [searchQuery]);

    const toggleRecipient = (id) => {
        setFormData(prev => {
            const list = prev.selectedRecipients || [];
            const newSelection = list.includes(id) ? list.filter(r => r !== id) : [...list, id];
            return { ...prev, selectedRecipients: newSelection };
        });
    };

    const handleSelectAll = (list, checked) => {
        setFormData(prev => {
            const currentSelection = new Set(prev.selectedRecipients || []);
            list.forEach(recipient => {
                if (checked) {
                    currentSelection.add(recipient.id);
                } else {
                    currentSelection.delete(recipient.id);
                }
            });
            return { ...prev, selectedRecipients: Array.from(currentSelection) };
        });
    };

    const generateNoteWithAI = () => {
        const planName = formData.planName || "[Plan Name]";
        const sponsorName = formData.sponsorName || "[Sponsor Name]";

        const note = `Hi Team,\n\nI'm sending over a proposal request for the ${planName} sponsored by ${sponsorName}. We are currently reviewing our options as part of our standard due diligence and would appreciate your team taking a look.\n\nAll the relevant details and plan documents are attached for your convenience. Please let me know if you have any initial questions or require further information.\n\nLooking forward to seeing what you can put together for us.\n\nBest,`;
        
        setFormData(prev => ({ ...prev, personalNote: note }));
    };
    
    return (
        <>
            <div className="mb-12">
                <h2 className="text-3xl font-bold text-slate-800">Choose Your Recipients</h2>
                <p className="mt-2 text-slate-600">Select the wholesalers and contacts who should receive this proposal request. You can search for anyone by name or email.</p>
                <div className="mt-8">
                   <InputField
                        name="recipientSearch"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search all recipients by name or email..."
                        icon={<Search/>}
                        iconPosition="leading"
                    />
                </div>
            </div>

            <RecipientGroup
                title="Trusted Wholesalers"
                subtitle="Your firm's pre-vetted, primary recordkeeping partners. An RFP request will go to their main intake desks."
                list={filteredWholesalers}
                selectedRecipients={selectedRecipients}
                onToggle={toggleRecipient}
                onSelectAll={handleSelectAll}
            />
            
            <RecipientGroup
                title="Special Partners"
                subtitle="Specialized partners for unique cases (e.g., large market, specific industries)."
                list={filteredPartners}
                selectedRecipients={selectedRecipients}
                onToggle={toggleRecipient}
                onSelectAll={handleSelectAll}
            />

            <RecipientGroup
                title="Personal Contacts"
                subtitle="Your individual contacts. The request will be sent directly to their personal email for direct communication."
                list={filteredContacts}
                selectedRecipients={selectedRecipients}
                onToggle={toggleRecipient}
                onSelectAll={handleSelectAll}
            />

            <div className="mt-12">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-slate-800">Add a Personal Note</h3>
                    <button 
                        type="button" 
                        onClick={generateNoteWithAI}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg shadow-sm hover:scale-105 transition-transform"
                    >
                        <Zap size={16} />
                        Generate with AI
                    </button>
                </div>
                <p className="mb-4 text-sm text-slate-500">
                    This note will be included in the body of the email sent to all selected recipients.
                </p>
                <textarea
                    name="personalNote"
                    rows="6"
                    value={formData.personalNote}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-300 text-slate-700 placeholder:text-slate-400"
                    placeholder="Example: Hi team, we're excited to see your proposal for this plan..."
                />
            </div>
        </>
    );
};

// --- END: Revamped Step 6 ---

const Step7_Submit = ({ handleSubmit }) => (
    <div className="text-center">
      <h2 className="text-3xl font-bold text-slate-800">Finalize & Send Your Proposal</h2>
      <p className="mt-4 max-w-2xl mx-auto text-slate-600">
        Review your selections and submit when ready. A confirmation will be sent to each selected recipient.
      </p>
      <div className="mt-12 flex justify-center">
        <button
          type="button"
          onClick={handleSubmit}
          className="group relative w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-xl transition-all duration-300 flex items-center justify-center space-x-3 font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105"
        >
          <Send size={22} />
          <span>Complete and Submit Request</span>
        </button>
      </div>
      <p className="text-xs text-slate-400 mt-4">By submitting, you agree to send the provided data to the selected recipients.</p>
    </div>
);

// Add dummy recipients lists
const TRUSTED_WHOLESALERS = [
    { id: 'tw1', name: 'Fidelity Institutional', email: 'wholesale.desk@fidelity.com' },
    { id: 'tw2', name: 'American Funds', email: 'rfp.intake@americanfunds.com' },
    { id: 'tw3', name: 'Empower Retirement', email: 'proposals@empower.com' },
    { id: 'tw4', name: 'Principal Financial Group', email: 'national.desk@principal.com' },
];
const SPECIAL_PARTNERS = [
    { id: 'sp1', name: 'BlackRock Elite Desk', email: 'elite.desk@blackrock.com' },
    { id: 'sp2', name: 'Vanguard Specialist Team', email: 'plan.specialist@vanguard.com' },
    { id: 'sp3', name: 'Nuveen Retirement Solutions', email: 'solutions@nuveen.com'},
];
const PERSONAL_CONTACTS = [
    { id: 'pc1', name: 'Jennifer Miller (Fidelity)', email: 'jennifer.miller@fidelityrep.com' },
    { id: 'pc2', name: 'Marcus Chen (American Funds)', email: 'm.chen@americanfunds.com' },
    { id: 'pc3', name: 'Samantha Rodriguez (Empower)', email: 's.rodriguez@empower.com'},
    { id: 'pc4', name: 'David Wilson (Your Assistant)', email: 'david.wilson@yourfirm.com'},
];

export default ProposalAutomation;